import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import {
  BookingHistoryApiResponseModel,
  BookingHistoryEntryModel,
  BookingRequestCreateModel,
  BookingRequestDetailsApiResponseModel,
  BookingRequestDetailsModel,
  BookingRequestPageParams,
  BookingRequestUpdateApiRequestModel,
  ChatMessageApiRequestModel,
  ChatMessageApiResponseModel,
  ChatMessageModel,
  ChatMessageTypeEnum,
  PagedResponse,
  RoleType,
  RoomBookingModel,
  RoomBookingStatusUpdateApiRequestModel,
  RoomBookingsApiResponseModel,
  environment,
} from '@bookly/shared';

import {
  bookingDetailsConverter,
  bookingHistoryConverter,
  roomBookingsConverter,
} from './booking-request.converter';

@Injectable({
  providedIn: 'root',
})
export class BookingRequestApiService {
  readonly #http = inject(HttpClient);
  readonly #bookingDetailsUrl = environment.admin.bookingDetailsApi;
  readonly #roomBookingsUrl = environment.admin.roomBookingsApi;
  readonly #userRoomBookingsUrl = environment.user.roomBookingApi;
  readonly #approveRoomBookingUrl = environment.admin.approveRoomBookingApi;
  readonly #rejectRoomBookingUrl = environment.admin.rejectRoomBookingApi;
  readonly #cancelRoomBookingUrl = environment.admin.cancelRoomBookingApi;
  readonly #bookingHistoryUrl = environment.admin.bookingHistoryApi;
  readonly #chatMessagesUrl = environment.shared.chatMessages;

  public getBookingDetails(
    bookingId: number
  ): Observable<BookingRequestDetailsModel> {
    return this.#http
      .get<BookingRequestDetailsApiResponseModel>(
        this.#bookingDetailsUrl + '/' + bookingId
      )
      .pipe(map(bookingDetails => bookingDetailsConverter(bookingDetails)));
  }

  public getBookingHistory(
    bookingId: number
  ): Observable<BookingHistoryEntryModel[]> {
    return this.#http
      .get<BookingHistoryApiResponseModel>(
        `${this.#bookingHistoryUrl}/${bookingId}`
      )
      .pipe(map(response => bookingHistoryConverter(response)));
  }

  public getRoomBookings(
    roleType: RoleType,
    pageParams: BookingRequestPageParams
  ): Observable<PagedResponse<RoomBookingModel>> {
    const params = this.prepareQueryParams(pageParams);
    return this.#http
      .get<
        PagedResponse<RoomBookingsApiResponseModel>
      >(`${this.#roomBookingsUrl}/${roleType}`, { params: params })
      .pipe(map(roomBookings => roomBookingsConverter(roomBookings)));
  }

  public createRoomBooking(
    request: BookingRequestCreateModel
  ): Observable<BookingRequestDetailsModel> {
    return this.#http
      .post<BookingRequestDetailsApiResponseModel>(
        this.#userRoomBookingsUrl,
        this.cleanObject(request)
      )
      .pipe(map(bookingDetails => bookingDetailsConverter(bookingDetails)));
  }

  public approveRoomBooking(request: RoomBookingStatusUpdateApiRequestModel) {
    return this.#http
      .put<BookingRequestDetailsApiResponseModel>(
        this.#approveRoomBookingUrl,
        request
      )
      .pipe(map(bookingDetails => bookingDetailsConverter(bookingDetails)));
  }

  public rejectRoomBooking(request: RoomBookingStatusUpdateApiRequestModel) {
    return this.#http
      .put<BookingRequestDetailsApiResponseModel>(
        this.#rejectRoomBookingUrl,
        request
      )
      .pipe(map(bookingDetails => bookingDetailsConverter(bookingDetails)));
  }

  public cancelRoomBooking(request: RoomBookingStatusUpdateApiRequestModel) {
    return this.#http
      .put<BookingRequestDetailsApiResponseModel>(
        this.#cancelRoomBookingUrl,
        request
      )
      .pipe(map(bookingDetails => bookingDetailsConverter(bookingDetails)));
  }

  public updateRoomBooking(
    request: BookingRequestUpdateApiRequestModel
  ): Observable<BookingRequestDetailsModel> {
    return this.#http
      .put<BookingRequestDetailsApiResponseModel>(
        this.#bookingDetailsUrl,
        this.cleanObject(request, [
          'customSetupTimeBefore',
          'customSetupTimeAfter',
        ])
      )
      .pipe(map(bookingDetails => bookingDetailsConverter(bookingDetails)));
  }

  public updateRoomBookingUser(
    request: BookingRequestUpdateApiRequestModel
  ): Observable<BookingRequestDetailsModel> {
    return this.#http
      .put<BookingRequestDetailsApiResponseModel>(
        this.#userRoomBookingsUrl,
        this.cleanObject(request)
      )
      .pipe(map(bookingDetails => bookingDetailsConverter(bookingDetails)));
  }

  public getMessages(
    bookingId: number,
    type: ChatMessageTypeEnum
  ): Observable<ChatMessageModel[]> {
    return this.#http.get<ChatMessageModel[]>(
      this.#chatMessagesUrl +
        bookingId +
        '/' +
        (type === ChatMessageTypeEnum.Chat ? 'chat' : 'work-notes')
    );
  }

  public markAllMessagesAsRead(bookingId: number) {
    return this.#http.put<ChatMessageModel[]>(
      this.#chatMessagesUrl + bookingId,
      null
    );
  }

  public postMessage(
    message: ChatMessageApiRequestModel,
    type: ChatMessageTypeEnum
  ) {
    return this.#http.post<ChatMessageApiResponseModel[]>(
      this.#chatMessagesUrl,
      { ...message, type }
    );
  }

  /**
   * Remove object entries that are strictly null or undefined, optionally preserving specified keys.
   * This will keep falsy-but-valid values like 0, false, and ''.
   * @param object - The object to clean
   * @param preserveKeys - Keys that should be kept even when their value is null/undefined
   */
  public cleanObject(object: object, preserveKeys: string[] = []) {
    const preserveSet = new Set(preserveKeys);

    return Object.fromEntries(
      Object.entries(object).filter(([key, value]) => {
        // keep values that are not null/undefined or keys explicitly requested to be preserved
        return (value !== null && value !== undefined) || preserveSet.has(key);
      })
    );
  }

  /**
   * Iterates over object keys and assings them as http params
   * When value is an array, it appends each value with the same key (lambda support)
   * @param obj
   */
  public prepareQueryParams(obj: object): HttpParams {
    let params = new HttpParams();
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (!Array.isArray(value)) {
          params = params.append(key, value);
        } else {
          Array.from(value).forEach(val => {
            params = params.append(key, val);
          });
        }
      }
    });
    return params;
  }
}
